import { MarcaCompania } from "@/componentes/impresos/MarcaCompania"
import { Ondas } from "@/componentes/impresos/Ondas"
import { cuerpoEnLinea, dato, filete, titular, versal } from "./estilo"
import { MEDIDAS } from "./medidas"
import type { DatosLomo } from "./tipos"

/** Lo que ocupan, a 60 mm de ancho, el encabezado y el pie de la tira. */
const CABEZA_Y_PIE = 70
/** El ancho medio de un carácter del título. */
const ANCHO_CARACTER = 0.58

/**
 * La etiqueta del lomo, con el orden de lectura del manual: código, departamento,
 * contenido y período, y la marca al pie.
 *
 * ⚠ **El título corre de abajo hacia arriba, como se lee un lomo en la estantería.** Lo
 * decide el manual (30/10) y revierte la tira con el texto de frente. El giro de 180°
 * no le cambia la caja al elemento, y `vertical-rl` sí la maqueta en vertical: por eso,
 * a diferencia de un `rotate(90deg)`, no reserva un hueco en el papel. Comprobado en PDF.
 *
 * ⚠ **Todo escala con el ancho, y el título además con el largo disponible**: la minuta
 * pide que sirva con cualquier medida. Si no cabe en una línea, pasa a dos antes de encoger.
 *
 * ⚠ **O de frente, si se elige** (03/11, pedido del usuario, `orientacion: "HORIZONTAL"`):
 * entonces va en las líneas que haga falta, partido por palabras, y el cuerpo lo decide la
 * palabra más larga —que tiene que caber entera en el ancho— y el alto disponible.
 */
export function cuerpoDelTitulo(titulo: string, ancho: number, alto: number): number {
  const k = Math.min(ancho / 60, 1.3)
  const base = 7.4 * k
  const disponible = alto * 0.906 - CABEZA_Y_PIE * k
  const largo = Math.max(titulo.length, 1) * ANCHO_CARACTER
  if (largo * base <= disponible) return base
  const enDos = (2 * disponible) / largo
  return Math.max(3, Math.min(base, enDos, (ancho * 0.7) / 2.2))
}

export function cuerpoHorizontal(titulo: string, ancho: number, alto: number): number {
  const k = Math.min(ancho / 60, 1.3)
  const base = 7.4 * k
  const util = ancho * 0.84
  const disponible = alto * 0.906 - CABEZA_Y_PIE * k
  const palabra = Math.max(...titulo.split(/\s+/).map((p) => p.length), 1)
  const caracteres = Math.max(titulo.length, 1)
  // Las líneas que salen a un cuerpo `c` son caracteres·0,58·c / util, y cada una mide 1,15·c.
  const porAlto = Math.sqrt((disponible * util) / (caracteres * ANCHO_CARACTER * 1.15))
  return Math.max(3, Math.min(base * 1.4, util / (palabra * ANCHO_CARACTER), porAlto))
}

export function LomoCarpeta({ datos }: { datos: DatosLomo }) {
  const ancho = datos.ancho_mm
  const alto = datos.alto_mm ?? MEDIDAS.LOMO.alto
  const k = Math.min(ancho / 60, 1.3)
  const util = ancho * 0.84

  return (
    <article
      className="pieza-impresa"
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: `${ancho}mm`,
        height: `${alto}mm`,
        paddingTop: `${alto * 0.094}mm`,
        fontFamily: "var(--font-rotulo-texto)",
      }}
      aria-label={`Lomo de carpeta: ${datos.titulo || "sin título"}`}
    >
      <Ondas pieza="lomo" style={{ left: 0, top: 0, width: "100%", height: `${alto * 0.094}mm` }} />

      {datos.siglas ? (
        <p
          className="tabular"
          style={titular(cuerpoEnLinea(datos.siglas, util, 9 * k), {
            marginTop: `${6.9 * k}mm`,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
          })}
        >
          {datos.siglas}
        </p>
      ) : null}
      {datos.subtitulo ? (
        <p
          style={versal(cuerpoEnLinea(datos.subtitulo, util / 1.3, 3.2 * k, 1.8), {
            marginTop: `${3.2 * k}mm`,
            textAlign: "center",
          })}
        >
          {datos.subtitulo}
        </p>
      ) : null}
      <div style={filete({ width: `${10.6 * k}mm`, marginTop: `${5.3 * k}mm` })} />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: `${4.8 * k}mm 0`,
        }}
      >
        {datos.orientacion === "HORIZONTAL" ? (
          <h2
            style={titular(cuerpoHorizontal(datos.titulo, ancho, alto), {
              width: `${util}mm`,
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              textAlign: "center",
              overflowWrap: "normal",
              hyphens: "none",
            })}
          >
            {datos.titulo}
          </h2>
        ) : (
          <h2
            style={titular(cuerpoDelTitulo(datos.titulo, ancho, alto), {
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
              letterSpacing: "-0.01em",
              textAlign: "center",
              maxHeight: "100%",
            })}
          >
            {datos.titulo}
          </h2>
        )}
      </div>

      {datos.contenido ? (
        <p
          style={dato(Math.max(1.9, 2.4 * k), {
            width: `${util}mm`,
            textAlign: "center",
            marginBottom: `${1.5 * k}mm`,
          })}
        >
          {datos.contenido}
        </p>
      ) : null}
      {datos.fecha ? (
        <p
          className="tabular"
          style={dato(cuerpoEnLinea(datos.fecha, util, 4.5 * k), {
            fontWeight: 600,
            color: "var(--impreso-vino)",
            whiteSpace: "nowrap",
          })}
        >
          {datos.fecha}
        </p>
      ) : null}
      <div style={filete({ width: `${10.6 * k}mm`, marginTop: `${4.8 * k}mm` })} />
      <div style={{ margin: `${4.8 * k}mm 0 ${5.3 * k}mm` }}>
        <MarcaCompania compania={datos.compania} alto={14.8 * k} />
      </div>
    </article>
  )
}
